/**
 * RunGraphTab — the Graph tab body.
 * Card wrapping <StepGraph> with header showing step count + failed-at label.
 * Running steps show elapsed time from step.startedAt using a 1s tick.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Workflow } from 'lucide-react';
import { StepGraph, type StepGraphStep } from '../../../components/ui/StepGraph.js';
import { FanOutSection } from './FanOutSection.js';
import { type DebugPayload, fanOutSummary, findFailedStep, formatDuration, mapStepStatus } from './run-detail-types.js';

interface RunGraphTabProps {
  payload: DebugPayload;
  activeStepId: string | null;
  onSelectStep: (id: string) => void;
}

export const RunGraphTab: React.FC<RunGraphTabProps> = ({ payload, activeStepId, onSelectStep }) => {
  const { graph, run } = payload;
  const steps = graph.steps;
  const failedStep = findFailedStep(steps);
  const isRunning = run.status === 'running';

  // 1-second ticker for live elapsed time on running steps
  const [tick, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning]);

  const mappedSteps: StepGraphStep[] = steps.map((step) => {
    const status = mapStepStatus(step.status);
    const verificationStatus = (step.verification as { status?: string })?.status;

    // Elapsed time for currently-running steps.
    // `void tick` creates an explicit render dependency on the 1s ticker without the
    // `tick * 0` pattern (which is always 0 and could be elided by optimisers/linters).
    void tick;
    let elapsedLabel: string | null = null;
    if (status === 'running' && step.startedAt) {
      const elapsedMs = Date.now() - new Date(step.startedAt).getTime();
      const elapsedS = Math.floor(elapsedMs / 1000);
      const m = Math.floor(elapsedS / 60);
      const s = elapsedS % 60;
      elapsedLabel = m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`;
    }

    // Right meta: duration + cost(gap) + retryCount
    const meta = (
      <>
        {status === 'running' && elapsedLabel ? (
          <span className="text-accent-text">live</span>
        ) : (
          formatDuration(step.durationMs)
        )}
        <br />
        {/* BACKEND GAP: per-step cost not available — render faint dash */}
        <span className="text-faint">—</span>
        {step.retryCount > 0 && (
          <>
            <br />
            <span className="text-faint">retry {step.retryCount}</span>
          </>
        )}
      </>
    );

    // Sub content: verification, error, running elapsed, skipped
    const sub = (() => {
      if (status === 'running' && elapsedLabel) {
        return <span className="text-accent-text">running… {elapsedLabel}</span>;
      }
      if (status === 'failed' && step.retryCount > 0 && step.error) {
        return (
          <>
            <span className="text-danger">failed · {step.retryCount} retries</span>
            <span>{step.error}</span>
          </>
        );
      }
      if (status === 'failed' && step.error) {
        return <span className="text-danger">{step.error}</span>;
      }
      if (status === 'skipped') {
        return <span>skipped</span>;
      }
      if (step.fanOut) {
        return <span>{fanOutSummary(step.fanOut.counts, step.fanOut.units.length)}</span>;
      }
      if (verificationStatus) {
        return <span>verified ✓</span>;
      }
      if (step.outputSummary) {
        return <span>{step.outputSummary}</span>;
      }
      return null;
    })();

    return {
      id: step.stepId ?? step.id,
      name: step.stepName,
      status,
      kind: step.kind,
      meta,
      sub,
    };
  });

  const headerMeta = failedStep
    ? `${steps.length} steps · failed at "${failedStep.stepName}"`
    : `${steps.length} steps`;

  return (
    <>
      <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4" style={{ margin: 0 }}>
        {/* Card header */}
        <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
          <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2">
            <Workflow size={14} className="text-faint" />
            Step graph
          </span>
          <span className="font-mono text-[10.5px] text-faint">{headerMeta}</span>
        </div>

        {steps.length === 0 ? (
          <div className="px-4 py-8 text-center text-[12.5px] text-muted">No steps recorded.</div>
        ) : (
          <StepGraph steps={mappedSteps} activeId={activeStepId ?? undefined} onSelect={onSelectStep} />
        )}
      </div>

      {/* Fan-out parent steps + child unit reports (Spec 82 S6). Renders nothing for
          non-fan-out runs, so existing runs are unaffected. */}
      <FanOutSection steps={steps} />
    </>
  );
};
